import React from 'react';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Pagination } from 'ats/components/pagination/Pagination';
import DrawerContainer from 'ats/components/drawer/DrawerContainer';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import FilterBar from 'ats/components/filter-bar/FilterBar';
import { buildAppliedFilterItems } from 'ats/components/filter-drawer/buildAppliedFilterItems';
import { ListStore } from 'shared/stores/ListStore';
import { externalApplicationStore } from './ExternalApplicationStore';
import ExternalApplicationsTable from './ExternalApplicationsTable';
import ExternalApplicationProfile from './ExternalApplicationProfile';
import EmptyState from 'ats/components/empty-state/EmptyState';
import { EMPTY_STATE_ICONS } from 'ats/components/empty-state/emptyStateIcons';
import { attachListProfileDrawerHandlers } from 'ats/utils/openEntityProfile';
import ViewsDropdown from 'ats/components/views-dropdown/ViewsDropdown';
import { ListViewController } from 'ats/components/views-dropdown/ListViewController';
import { LIST_VIEW_SECTIONS } from 'ats/components/views-dropdown/listViewSections';
import { EXTERNAL_APPLICATION_STATUS_FILTER_OPTIONS } from '../open-jobs/externalApplicationOptions';

@withRouter
@observer
export class ExternalApplications extends React.Component {
	state = {
		searchFilter: null,
		activeApplicationId: null,
		clientFilter: null,
		jobRoleFilter: [],
		jobFilter: null,
		statusFilter: EXTERNAL_APPLICATION_STATUS_FILTER_OPTIONS[0],
		visibleColumns: externalApplicationStore.defaultTableColumnsOptions
	};

	constructor(props) {
		super(props);
		this.listStore = new ListStore(externalApplicationStore, {
			entityName: 'External Application'
		});
		this.listViewController = new ListViewController(
			LIST_VIEW_SECTIONS.EXTERNAL_APPLICATIONS,
			this
		);
	}

	async componentDidMount() {
		await this.listViewController.initialize();

		const applicationIdFromUrl = this.props.match?.params?.id;
		if (applicationIdFromUrl) {
			this.openProfileById(applicationIdFromUrl);
		}
	}

	componentWillUnmount() {
		this.listStore.store.resetQuery();

		if (drawerStore.isOpen) {
			drawerStore.closeDrawer();
		}
	}

	componentDidUpdate(prevProps) {
		const prevId = prevProps.match?.params?.id;
		const nextId = this.props.match?.params?.id;

		if (prevId === nextId) return;

		if (nextId) {
			this.openProfileById(nextId);
		} else {
			if (
				drawerStore.isOpen &&
				drawerStore.drawerId ===
					drawerStore.DRAWER_IDS.EXTERNAL_APPLICATION_PROFILE
			) {
				drawerStore.closeDrawer();
			}
			this.setState({ activeApplicationId: null });
		}
	}

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	getBaseUrl = () => {
		const url = this.props.match?.url || '';
		const id = this.props.match?.params?.id;
		if (!id) return url;
		return url.replace(new RegExp(`/${id}$`), '');
	};

	openProfileById = applicationId => {
		this.setState({ activeApplicationId: applicationId });

		const onClose = () => {
			this.setState({ activeApplicationId: null });
			drawerStore.closeDrawer();
			this.props.history.push(this.getBaseUrl());
		};
		const onSuccess = () => this.listStore.search();

		if (
			attachListProfileDrawerHandlers({
				drawerId: drawerStore.DRAWER_IDS.EXTERNAL_APPLICATION_PROFILE,
				idKey: 'applicationId',
				entityId: applicationId,
				onClose,
				onSuccess
			})
		) {
			return;
		}

		drawerStore.openDrawer(
			{
				containerId: 'external-applications',
				drawerId: drawerStore.DRAWER_IDS.EXTERNAL_APPLICATION_PROFILE,
				showHeader: false,
				props: {
					applicationId,
					onSuccess,
					onClose,
					onDecision: this.handleDecision
				}
			},
			ExternalApplicationProfile
		);
	};

	openProfile = rowData => {
		const baseUrl = this.getBaseUrl();
		this.props.history.push(`${baseUrl}/${rowData.id}`);
		this.openProfileById(rowData.id);
	};

	handleDecision = async () => {
		await this.listStore.search();

		const remaining = this.listStore.data || [];
		const nextPending = remaining.find(
			row =>
				row.status === 'pending' &&
				String(row.id) !== String(this.state.activeApplicationId)
		);

		if (nextPending) {
			this.openProfile(nextPending);
			return;
		}

		if (
			this.state.statusFilter?.value &&
			this.state.statusFilter.value !== 'pending'
		) {
			if (this.state.activeApplicationId) {
				this.openProfileById(this.state.activeApplicationId);
			}
			return;
		}

		this.setState({ activeApplicationId: null });
		drawerStore.closeDrawer();
		this.props.history.push(this.getBaseUrl());
	};

	getBarFilters = () => {
		return [
			{
				type: 'search',
				label: 'Search',
				name: 'search',
				onKeyPress: newValue => {
					this.setState({
						searchFilter: newValue
					});
				},
				onChange: newValue => {
					this.listStore.store.setQuery('text', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.searchFilter
			},
			{
				type: 'client-dropdown',
				label: 'Client',
				isMulti: true,
				isClearable: true,
				placeholder: 'Client: All',
				onChange: newValue => {
					this.setState({ clientFilter: newValue });
					this.listStore.store.setQuery(
						'clientId',
						(newValue || []).map(item => item.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.clientFilter
			},
			{
				type: 'job-role-dropdown',
				label: 'Applicant Role',
				placeholder: 'Applicant Role: All',
				onChange: newValue => {
					this.setState({ jobRoleFilter: newValue });
					this.listStore.store.setQuery(
						'jobRoleIds',
						(newValue || []).map(item => item.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.jobRoleFilter
			},
			{
				type: 'job-position-dropdown',
				label: 'Job',
				placeholder: 'Job: All',
				onChange: newValue => {
					this.setState({ jobFilter: newValue });
					this.listStore.store.setQuery('jobId', newValue?.id || null);
					this.handlePaginationChange(1);
				},
				value: this.state.jobFilter
			},
			{
				type: 'dropdown',
				name: 'status',
				placeholder: 'Status: All',
				options: EXTERNAL_APPLICATION_STATUS_FILTER_OPTIONS,
				onChange: newValue => {
					this.setState({ statusFilter: newValue });
					this.listStore.store.setQuery('status', newValue?.value || null);
					this.handlePaginationChange(1);
				},
				value: this.state.statusFilter
			}
		];
	};

	getAppliedFilters = () => buildAppliedFilterItems(this.getBarFilters());

	resetDrawerFilters = () => {
		this.setState({
			searchFilter: '',
			clientFilter: null,
			jobRoleFilter: [],
			jobFilter: null,
			statusFilter: EXTERNAL_APPLICATION_STATUS_FILTER_OPTIONS[0]
		});

		this.listStore.store.resetQuery();
		this.handlePaginationChange(1);
	};

	renderEmptyState = () => {
		const hasFilters = this.getAppliedFilters().length > 0;
		const showingArchived = ['approved', 'rejected'].includes(
			this.state.statusFilter?.value
		);

		return (
			<EmptyState
				icon={
					hasFilters
						? EMPTY_STATE_ICONS.noResultsCandidates
						: EMPTY_STATE_ICONS.candidate
				}
				iconAlt={
					hasFilters
						? 'No matching applications'
						: showingArchived
							? 'No archived applications'
							: 'No external applications'
				}
				message={
					hasFilters
						? 'No applications match your search criteria.'
						: showingArchived
							? 'No archived applications yet.'
							: 'No external applications to review.'
				}
				detail={
					hasFilters
						? 'Try adjusting your filters or search terms to see more results.'
						: showingArchived
							? 'Approved and rejected applications will appear here.'
							: 'Applications submitted through the Jobs Portal will appear here.'
				}
				actionText={hasFilters ? 'Clear filters' : undefined}
				onActionClick={hasFilters ? this.resetDrawerFilters : undefined}
			/>
		);
	};

	render() {
		const isProfileOpen =
			drawerStore.isOpen &&
			[
				drawerStore.DRAWER_IDS.EXTERNAL_APPLICATION_PROFILE,
				drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
				drawerStore.DRAWER_IDS.CLIENT_PROFILE
			].includes(drawerStore.drawerId);

		return (
			<div className="ExternalApplications">
				<div className="header-container">
					<h2>External Applicants</h2>
					<ViewsDropdown
						section={LIST_VIEW_SECTIONS.EXTERNAL_APPLICATIONS}
						controller={this.listViewController}
						viewStateKey={this.listViewController.getViewStateKey()}
						isModified={this.listViewController.isModified()}
					/>
					<FilterBar
						filters={this.getBarFilters()}
						appliedFilters={this.getAppliedFilters()}
						showFiltersButton={false}
					/>
				</div>

				<main>
					<div
						className={`external-applications-wrapper${
							isProfileOpen ? ' is-profile-open' : ''
						}`}>
						<div className="external-applications-container">
							<div className="ats-table-wrapper">
								<ExternalApplicationsTable
									data={this.listStore.data}
									sort={this.listStore.store.query.sort}
									onSort={this.listStore.handleSort}
									onSuccess={() => this.listStore.search()}
									onClickRow={this.openProfile}
									activeRowId={this.state.activeApplicationId}
									visibleColumns={this.state.visibleColumns}
									onVisibleColumnsChange={visibleColumns =>
										this.setState({ visibleColumns })
									}
									isEmpty={
										!this.listStore.loading && this.listStore.data.length === 0
									}
									emptyState={this.renderEmptyState()}
								/>
							</div>
						</div>
						<Pagination
							loading={this.listStore.loading}
							pagination={this.listStore.store.query.pagination}
							totalResults={this.listStore.store.query.pagination.totalResults}
							onPaginationChange={this.handlePaginationChange}
						/>
					</div>
					<DrawerContainer id="external-applications" />
				</main>
			</div>
		);
	}
}
